'use client';

import { useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'invisible';
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface TurnstileRef {
  reset: () => void;
}

interface TurnstileProps {
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

/**
 * Cloudflare Turnstile captcha widget.
 *
 * Renders an invisible/managed captcha. Calls `onToken` when
 * a valid captcha token is obtained.
 *
 * Use the ref to call `reset()` after a failed server-side validation
 * so the widget issues a fresh challenge token.
 *
 * In development, if NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set,
 * renders nothing (backend skips validation in dev mode too).
 */
export const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(
  function Turnstile({ onToken, onExpire, onError }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    const renderWidget = useCallback(() => {
      if (!siteKey || !window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': onExpire,
        'error-callback': onError,
        theme: 'auto',
        size: 'normal',
      });
    }, [siteKey, onToken, onExpire, onError]);

    useEffect(() => {
      if (!siteKey) return;

      // Load the Turnstile script if not already loaded
      if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => renderWidget();
        document.head.appendChild(script);
      } else if (window.turnstile) {
        // Script already loaded — render immediately
        renderWidget();
      } else {
        // Script tag exists but hasn't loaded yet — poll briefly
        const interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval);
            renderWidget();
          }
        }, 100);
        return () => clearInterval(interval);
      }
    }, [siteKey, renderWidget]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, []);

    // Don't render anything in dev without a site key
    if (!siteKey) return null;

    return <div ref={containerRef} className="flex justify-center" />;
  },
);
